import { useRef, useCallback, useEffect, useState } from 'react';
import { XIcon } from '../icons';

const MAX_FILES = 3;
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

interface ImageAttachmentProps {
  files: File[];
  onChange: (files: File[]) => void;
  onEditImage?: (index: number) => void;
}

export default function ImageAttachment({ files, onChange, onEditImage }: ImageAttachmentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // 파일 목록이 바뀔 때마다 ObjectURL 재생성 및 이전 URL 해제
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const addFiles = useCallback(
    (incoming: FileList | null) => {
      if (!incoming) return;
      const valid: File[] = [];
      Array.from(incoming).forEach((f) => {
        if (!ALLOWED_TYPES.includes(f.type)) return;
        if (f.size > MAX_SIZE_BYTES) return;
        valid.push(f);
      });
      const next = [...files, ...valid].slice(0, MAX_FILES);
      onChange(next);
    },
    [files, onChange],
  );

  const remove = (index: number) => {
    const next = files.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="flex flex-col gap-2">
      {/* 드래그 앤 드롭 영역 */}
      {files.length < MAX_FILES && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-ink-300 rounded-card p-4 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition-colors"
        >
          <p className="text-sm text-ink-500">
            클릭하거나 이미지를 드래그하여 추가
          </p>
          <p className="text-xs text-ink-400 mt-1">
            JPEG, PNG · 최대 5MB · {MAX_FILES - files.length}개 추가 가능
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>
      )}

      {/* 미리보기 그리드 */}
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {files.map((_file, index) => (
            <div key={index} className="relative group aspect-square rounded-card-sm overflow-hidden bg-surface-hover">
              <img
                src={previewUrls[index] ?? ''}
                alt={`첨부 이미지 ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {/* 메모 편집 오버레이 */}
              {onEditImage && (
                <button
                  type="button"
                  onClick={() => onEditImage(index)}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end justify-center pb-2 w-full"
                  aria-label={`이미지 ${index + 1} 메모 편집`}
                >
                  <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-2 py-1 rounded-full">
                    메모 편집
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(index)}
                className="absolute top-1 right-1 w-5 h-5 bg-ink-900/60 text-white rounded-full flex items-center justify-center hover:bg-ink-900/80 z-10"
                aria-label="이미지 삭제"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
